<template>
  <div class="ranking-answer">
    <div class="ranking-label">Jawaban</div>
    <ValidationProvider
      ref="validation"
      :vid="`rankingtype${indexSection}${indexQuestion}`"
      name="Ranking"
      :rules="
        checkRulesData(
          data.isRequired,
          data.leftSideNumber,
          data.rightSideNumber
        )
      "
      v-slot="{ errors }"
    >
      <div class="ranking-columns">
        <div class="ranking-options">
          <p class="ranking-instruction">
            Klik pernyataan di bawah sesuai dengan urutan yang Anda kehendaki.
          </p>
          <div class="ranking-list">
            <button
              v-for="ranking in options"
              :key="ranking.id"
              type="button"
              class="ranking-option notranslate"
              :class="{ 'is-selected': isSelected(ranking) }"
              :aria-pressed="isSelected(ranking)"
              @click="toggleOption(ranking)"
            >
              <span v-if="ranking.type === 'text'">
                {{ ranking.description }}
              </span>
              <img
                v-else
                :src="imageUrl(ranking.imagePath)"
                :alt="ranking.description || 'Pilihan gambar'"
                class="ranking-image"
              />
            </button>
          </div>
        </div>
        <div class="ranking-arrange">
          <div class="ranking-header">
            <p>Tahan dan geser untuk mengganti urutan pernyataan</p>
            <button type="button" class="ranking-reset" @click="clearAnswer">
              Reset Jawaban
            </button>
          </div>
          <div v-if="!answerRanking.length" class="ranking-empty">
            Klik pernyataan di samping untuk mulai mengurutkan
          </div>
          <draggable
            v-model="answerRanking"
            class="ranking-list ranking-ordered"
            draggable=".ranking-card"
            ghost-class="ranking-ghost"
            chosen-class="ranking-chosen"
            :animation="150"
            :delay="200"
            :delay-on-touch-only="true"
            :touch-start-threshold="5"
            @change="submit"
          >
            <div
              v-for="(ranking, index) in answerRanking"
              :key="ranking.id"
              class="ranking-card notranslate"
              tabindex="0"
              :aria-label="
                `Urutan ${index + 1}: ${ranking.description ||
                  'Pilihan gambar'}. Gunakan panah atas atau bawah untuk mengubah urutan.`
              "
              @keydown.up.prevent="moveAnswer(index, -1)"
              @keydown.down.prevent="moveAnswer(index, 1)"
            >
              <font-awesome-icon
                :icon="gripIcon"
                class="ranking-grip"
                aria-hidden="true"
              />
              <span class="ranking-number">{{ index + 1 }}</span>
              <span v-if="ranking.type === 'text'" class="ranking-description">
                {{ ranking.description }}
              </span>
              <img
                v-else
                :src="imageUrl(ranking.imagePath)"
                :alt="ranking.description || 'Pilihan gambar'"
                class="ranking-image"
              />
            </div>
          </draggable>
        </div>
      </div>
      <div v-if="errors[0]" class="required ranking-error" role="alert">
        {{ errors[0] }}
      </div>
    </ValidationProvider>
    <info-storing-answer
      :question="data"
      @resend="$emit('resend-answer', data)"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faGripLinesVertical } from '@fortawesome/free-solid-svg-icons'
import InfoStoringAnswer from '@/components/V2/Candidat/InfoStoringAnswer.vue'

export default {
  components: { draggable, FontAwesomeIcon, InfoStoringAnswer },
  props: {
    data: { default: () => ({}) },
    indexSection: { default: null },
    indexQuestion: { default: null },
    sectionId: { default: null },
  },
  data() {
    return { options: [], answerRanking: [], gripIcon: faGripLinesVertical }
  },
  watch: {
    data: {
      immediate: true,
      handler(data) {
        // Keep the source list intact; only the answer list is reordered.
        this.options = (data.answerList || []).map(answer => ({ ...answer }))
        this.answerRanking = this.options
          .filter(answer => Number(answer.indexAnswer) > 0)
          .sort((a, b) => Number(a.indexAnswer) - Number(b.indexAnswer))
        this.$nextTick(() => {
          if (this.$refs.validation) {
            this.$refs.validation.syncValue(this.answerRanking)
            this.$refs.validation.reset()
          }
        })
      },
    },
  },
  methods: {
    isSelected(option) {
      return this.answerRanking.some(answer => answer.id === option.id)
    },
    imageUrl(path) {
      if (!path || /^(https?:|data:|blob:|\/\/)/i.test(path)) return path || ''
      return (
        (process.env.VUE_APP_BACKEND_URL || '').replace(/\/$/, '') +
        '/' +
        path.replace(/^\//, '')
      )
    },
    toggleOption(option) {
      if (this.isSelected(option)) {
        this.answerRanking = this.answerRanking.filter(
          answer => answer.id !== option.id
        )
      } else {
        this.answerRanking = [...this.answerRanking, option]
      }
      this.submit()
    },
    moveAnswer(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.answerRanking.length) return
      const answers = this.answerRanking.slice()
      const [answer] = answers.splice(index, 1)
      answers.splice(target, 0, answer)
      this.answerRanking = answers
      this.submit()
    },
    clearAnswer() {
      if (!this.answerRanking.length) return
      this.answerRanking = []
      this.submit()
    },
    // Called by the parent's media prerequisite guard: clear locally without
    // emitting another save that would re-enter the same guard.
    resetAnswer() {
      this.answerRanking = []
      this.$refs.validation.syncValue(this.answerRanking)
      this.$refs.validation.reset()
    },
    submit() {
      this.$refs.validation.validate(this.answerRanking)
      // Preserve the API's disjoint available/selected lists and take snapshots
      // so subsequent interactions cannot mutate an in-flight or retry payload.
      const available = this.options.filter(option => !this.isSelected(option))
      const answers = this.answerRanking.map(answer => ({ ...answer }))
      this.$emit('clicked', {
        newSectionId: this.sectionId,
        newQuestionId: this.data.id,
        newAnswerId: answers,
        description: null,
        rating: 0,
        indexQuestion: available.map(answer => ({ ...answer })),
        indexAnswer: answers,
      })
    },
    checkRulesData(isRequired, minimumNumber, maximumNumber) {
      let rules = ``
      if (isRequired) {
        rules = `requiredarray`
      }

      if (minimumNumber != null) {
        if (rules != ``) {
          rules = rules + `|`
        }
        rules = rules + `checkboxmin:${minimumNumber}`
      }

      if (maximumNumber != null) {
        if (rules != ``) {
          rules = rules + `|`
        }
        rules = rules + `checkboxmax:${maximumNumber}`
      }

      return rules
    },
  },
}
</script>

<style scoped>
.ranking-answer {
  font-family: 'Segoe UI', sans-serif;
  color: #423939;
}
.ranking-label {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}
.ranking-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}
.ranking-options {
  background: #f0f1f2;
  border: 1px solid #bdbcbc;
  border-radius: 8px;
  padding: 12px 16px;
}
.ranking-instruction {
  font-size: 14px;
  font-weight: 300;
  line-height: normal;
  margin: 0 0 16px;
}
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.ranking-option,
.ranking-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 10px 8px;
  background: white;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  overflow-wrap: anywhere;
}
.ranking-option {
  display: flex;
  align-items: center;
  min-height: 30px;
  text-align: left;
  color: #423939;
  cursor: pointer;
}
.ranking-option.is-selected {
  background: #f9fafb;
  border: 2px solid #81d9b3;
  color: #757575;
}
.ranking-arrange {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 672px;
  border: 2px solid #01c19e;
  border-radius: 8px;
  padding: 12px;
}
.ranking-header {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 10px;
  align-items: flex-start;
}
.ranking-header p {
  flex: 1;
  min-width: 100px;
  margin: 0;
  color: #757575;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
}
.ranking-reset {
  white-space: nowrap;
  flex: none;
  width: 101px;
  padding: 0 4px;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: #01c19e;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
}
.ranking-empty {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #9f9f9f;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
}
.ranking-ordered:empty {
  display: none;
}
.ranking-card {
  display: flex;
  align-items: center;
  gap: 8px;
  border-color: #d9d9d9;
  color: #212121;
  cursor: grab;
}
.ranking-grip {
  flex: none;
  width: 16px;
  height: 16px;
  color: #9f9f9f;
}
.ranking-number {
  flex: none;
  width: 20px;
  text-align: center;
  font-weight: 600;
}
.ranking-description {
  flex: 1;
  min-width: 0;
}
.ranking-image {
  display: block;
  width: 180px;
  height: 180px;
  max-width: 100%;
  object-fit: contain;
  min-width: 0;
}
.ranking-ghost {
  background: #e6f9f2;
  border: 2px dashed #81d9b3;
}
.ranking-ghost > * {
  visibility: hidden;
}
.ranking-chosen {
  cursor: grabbing;
}
.ranking-option:focus-visible,
.ranking-card:focus-visible,
.ranking-reset:focus-visible {
  outline: 2px solid #01c19e;
  outline-offset: 2px;
}
.ranking-error {
  margin-top: 12px;
}
@media screen and (max-width: 768px) {
  .ranking-columns {
    gap: 8px;
  }
  .ranking-option,
  .ranking-card,
  .ranking-instruction,
  .ranking-header p,
  .ranking-empty {
    font-size: 12px;
  }
  .ranking-arrange {
    align-self: stretch;
  }
  .ranking-card {
    align-items: flex-start;
  }
  .ranking-grip {
    display: none;
  }
  .ranking-number {
    width: auto;
  }
}
</style>
